import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';

import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store, createSelector } from '@ngrx/store';
import { NumpadService } from 'packages/sports/web/app/src/numpad/numpad.service';
import { Observable } from 'rxjs';

import { ILinearBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from '../../../../base/services/betslip-messages-provider.service';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { SlipId } from '../../../bet-generation/models';
import { getSystemSlipId } from '../../../bet-generation/services/slip.utils';
import { IBetslipInfoMessage } from '../../../common/models';
import { InfoMessagesActions } from '../../../info-messages//actions';
import { selectHasEachWayPick } from '../../../picks/selectors';
import { LINEAR_CONTAINER_STAKE_SERVICE } from '../../../stake/services/digital/linear/linear-container-stake.interface';
import { LINEAR_CONTAINER_SUMMARY_SERVICE } from '../../../summary/services/linear/linear-container-summary.interface';
import { InfoTrackingContext } from '../../../tracking/models';
import LinearBetslipTrackingActions from '../../../tracking/state/linear-tracking.actions';
import BetslipSystemBetActions from '../../actions';
import { ISystemSlipTypeInfo } from '../../models';
import { systemBetTypeStakeSelectorFactory } from '../../selectors';
import { SystemBetTypeContainerStakeService } from '../../services/stake/system-bet-type-container-stake.service';
import { SystemBetTypeContainerSummaryService } from '../../services/summary/system-bet-type-container-summary.service';
import SystemBetTypesPopUpInfoService from '../../services/types/system-bet-types-popup-info.service';

const selectIsTypeEachWay = (typeKey: string) =>
    createSelector(systemBetTypeStakeSelectorFactory(typeKey), (typeStake) => typeStake?.isEachWay ?? false);

@Component({
    selector: 'bs-system-bet-type-container',
    templateUrl: './system-bet-type-container.component.html',
    providers: [
        NumpadService,
        SystemBetTypeContainerStakeService,
        SystemBetTypeContainerSummaryService,
        { provide: LINEAR_CONTAINER_STAKE_SERVICE, useExisting: SystemBetTypeContainerStakeService },
        { provide: LINEAR_CONTAINER_SUMMARY_SERVICE, useExisting: SystemBetTypeContainerSummaryService },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./system-bet-type-container.component.scss'],
    standalone: false,
})
export default class SystemBetTypeContainerComponent implements OnInit {
    @Input() systemTypeInfo: ISystemSlipTypeInfo;
    @Input() isEpcotEnabled: boolean;
    @Input() oddsDisplayFormat: OddsDisplayFormat;

    messages: ILinearBetslipMessages;
    betslipInfoMessage: IBetslipInfoMessage;
    slipId: SlipId;
    betslipType = BetslipType.System;
    isEachWay$: Observable<boolean>;
    isEachWayButtonVisible$: Observable<boolean>;

    constructor(
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: ILinearBetslipMessagesProviderService,
        private stakeService: SystemBetTypeContainerStakeService,
        private summaryService: SystemBetTypeContainerSummaryService,
        private popupInfoService: SystemBetTypesPopUpInfoService,
        private store: Store<IBetslipRootState>,
    ) {
        this.messages = messageProvider.getMessages();
        this.isEachWayButtonVisible$ = this.store.select(selectHasEachWayPick);
    }

    ngOnInit(): void {
        this.slipId = getSystemSlipId(this.systemTypeInfo.slipType);
        this.stakeService.initialize(this.systemTypeInfo);
        this.summaryService.initialize(this.systemTypeInfo);
        this.betslipInfoMessage = this.popupInfoService.getPopUpInfoByTypeInfo(this.systemTypeInfo);
        this.isEachWay$ = this.store.select(selectIsTypeEachWay(this.systemTypeInfo.slipType.key));
    }

    getTitle(): string {
        return this.systemTypeInfo.typeName;
    }

    infoMessageId(): string {
        return this.systemTypeInfo.typeName;
    }

    toggleSystemInfo(): void {
        this.store.dispatch(InfoMessagesActions.toggle({ messageId: this.infoMessageId() }));

        this.store.dispatch(
            LinearBetslipTrackingActions.clickInfoIcon({
                messageId: this.infoMessageId(),
                infoContext: InfoTrackingContext.SystemBet,
                additionalTypeContext: this.systemTypeInfo.slipType.key,
            }),
        );
    }

    toggleEachWay(isToggled: boolean): void {
        this.store.dispatch(BetslipSystemBetActions.setSystemBetTypeEachWay({ isEachWay: isToggled, systemType: this.systemTypeInfo.slipType }));
    }
}
